import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { clearFormAction } from 'Redux/forms/formsActions';
import { updateProfileSettings } from 'Redux/profile/profileThunks';
import { LOADING } from 'Redux/userInterface/userInterfaceConstants';
import { H2, FlexPanel, Button } from 'Common';
import { showToast } from 'Utils/toastUtils';
import DynamicFormContainerModal from 'ExtendedForm/DynamicForm/DynamicFormContainerModal';
import { withOptimizely } from 'Components/Optimizely/Optimizely';
import { t } from 'Utils/localization/i18next';
import { trimAllObjPropertiesSkipPasswords } from 'Utils/object/objectUtils';
import { isEditModeEnabled } from 'Utils/url/urlUtils';
import pencil from '@ingka/ssr-icon/paths/pencil';
import styles from './CommunicationPreferences.module.scss';

class NewsletterSubscriptionSectionReadContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: isEditModeEnabled(window.location.search, 'consent'),
      optimizelyDecisionEnabled: false
    };
    this.myRef = React.createRef();
  }

  componentDidUpdate() {
    this.setState({
      optimizelyDecisionEnabled: this.props.decide('profile_consent_boost')
        ?.enabled
    });
  }

  sendOptimizelyEvent = (event, eventValue) => {
    if (this.state.optimizelyDecisionEnabled) {
      this.props.trackEvent(event, eventValue);
    }
  };

  handleBoostConsentTest = (oldProfile, newProfile) => {
    if (this.state.optimizelyDecisionEnabled) {
      this.sendOptimizelyEvent('consent_modal_submitted');
      if (
        (!oldProfile.allowEmail && newProfile.allowEmail) ||
        (!oldProfile.allowSMS && newProfile.allowSMS) ||
        (!oldProfile.allowDirectMail && newProfile.allowDirectMail)
      ) {
        this.sendOptimizelyEvent('consent_signup');
      }

      if (!oldProfile.allowEmail && newProfile.allowEmail) {
        this.sendOptimizelyEvent('consent_allow_email_opt_in');
      }
      if (oldProfile.allowEmail && !newProfile.allowEmail) {
        this.sendOptimizelyEvent('consent_allow_email_opt_out');
      }

      if (!oldProfile.allowSMS && newProfile.allowSMS) {
        this.sendOptimizelyEvent('consent_allow_sms_opt_in');
      }
      if (oldProfile.allowSMS && !newProfile.allowSMS) {
        this.sendOptimizelyEvent('consent_allow_sms_opt_out');
      }

      if (!oldProfile.allowDirectMail && newProfile.allowDirectMail) {
        this.sendOptimizelyEvent('consent_allow_direct_mail_opt_in');
      }
      if (oldProfile.allowDirectMail && !newProfile.allowDirectMail) {
        this.sendOptimizelyEvent('consent_allow_direct_mail_opt_out');
      }
    }
  };

  onEdit = () => {
    this.sendOptimizelyEvent('consent_edit_clicked');
    this.setState({ isEditing: true });
  };

  onClose = () => {
    this.sendOptimizelyEvent('consent_modal_closed');
    this.setState({ isEditing: false }, () => {
      this.props.clearFormAction(this.props.editForm);
      this.scrollToCenter();
    });
  };

  onSubmit = async data => {
    const currentProfile = { ...this.props.profile };
    const newProfile = { ...this.props.profile, ...data };
    const success = await this.props.updateProfileSettings(
      trimAllObjPropertiesSkipPasswords(newProfile),
      this.props.editForm,
      true
    );
    if (success) {
      this.handleBoostConsentTest(currentProfile, newProfile);
      showToast(
        t('dashboard.confirmation.communicationSectionUpdateTitle'),
        t('dashboard.confirmation.communicationSectionUpdateText')
      );
      this.setState({ isEditing: false });
    }
    this.scrollToCenter();
  };

  scrollToCenter = () => {
    if (this.myRef) {
      this.myRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  setRef = node => {
    if (node) {
      this.myRef = node;
    }
  };

  render() {
    return (
      <React.Fragment>
        <div ref={this.setRef} className={styles['section-read']}>
          <FlexPanel spread className={styles['section-read-title']}>
            <H2 tagName="h2" headingSize="m" margin={false}>
              {this.props.generalPreferences
                ? t(`dashboard.communications.general-preferences.title`)
                : t(`dashboard.communications.newsletter-section.title`)}
            </H2>
            {this.props.editForm && (
              <>
                <Button
                  className={styles['edit-link']}
                  size="small"
                  type="tertiary"
                  id={`${this.props.editForm}-link`}
                  onClick={this.onEdit}
                  ssrIcon={pencil}
                >
                  {this.props.generalPreferences
                    ? t('dashboard.account.edit.general-preferences')
                    : t('dashboard.account.edit.communication')}
                </Button>
                <Button
                  id={`${this.props.editForm}-link-mobile`}
                  className={styles['edit-link-mobile']}
                  onClick={this.onEdit}
                  size="small"
                  type="tertiary"
                  aria-label={
                    this.props.generalPreferences
                      ? t('dashboard.account.edit.general-preferences')
                      : t('dashboard.account.edit.communication')
                  }
                  ssrIcon={pencil}
                  iconOnly
                ></Button>
              </>
            )}
          </FlexPanel>
          {this.state.isEditing && (
            <DynamicFormContainerModal
              formId={this.props.editForm}
              prefillValues={this.props.profile}
              loadingSubmit={this.props.isLoading}
              textSubmit={t('common.save')}
              textCancel={t('common.cancel')}
              onCancel={this.onClose}
              onSubmit={this.onSubmit}
              title={
                this.props.generalPreferences
                  ? t(`dashboard.communications.general-preferences.title`)
                  : t(`dashboard.communications.newsletter-section.title`)
              }
            />
          )}
          {this.props.children}
        </div>
      </React.Fragment>
    );
  }
}

NewsletterSubscriptionSectionReadContainer.propTypes = {
  editForm: PropTypes.string,
  title: PropTypes.string,
  generalPreferences: PropTypes.bool,
  profile: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]),
  // From Redux/Thunks
  updateProfileSettings: PropTypes.func.isRequired,
  clearFormAction: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  trackEvent: PropTypes.func,
  decide: PropTypes.func
};

const mapStateToProps = state => {
  return {
    profile: state.profile,
    isLoading: state.userInterface.loading[LOADING.PROFILE_UPDATE]
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      updateProfileSettings,
      clearFormAction
    },
    dispatch
  );
};

const newsletterSubscriptionContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(NewsletterSubscriptionSectionReadContainer);

export default withOptimizely(newsletterSubscriptionContainer);
